import { useParams } from "react-router-dom";

import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import ResponsiveImage from "../../../shared/components/elements/ResponsiveImage";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import Breadcrumb from "../../../shared/components/layout/Breadcrumb";
import PageLayout from "../../../shared/components/layout/PageLayout";
import SEO from "../../../shared/components/layout/SEO";
import Wrapper from "../../../shared/components/layout/Wrapper";

import CourseDifficulty from "../components/CourseDifficulty";
import CourseFaq from "../components/CourseFaq";
import CourseSyllabus from "../components/CourseSyllabus";
import PurchasePanel from "../components/PurchasePanel";
import { usePurchaseCourse } from "../hooks/usePurchaseCourse";
import { COURSE_MESSAGES } from "../messages";

const PurchaseCoursePage = () => {
    const { course_id } = useParams<{ course_id: string }>();
    const { lang } = useLanguage();
    const { data: course, loading, error } = usePurchaseCourse(course_id);

    // TODO: start the ZarinPal payment here. It needs a backend endpoint that creates
    // the payment and returns the gateway URL, then: window.location.assign(gatewayUrl)
    const handlePay = () => {};

    if (loading || error || !course) {
        const message = loading
            ? COURSE_MESSAGES.loadingCourse
            : error
                ? COURSE_MESSAGES.courseFailed
                : COURSE_MESSAGES.courseNotFound;

        return (
            <PageLayout>
                <Wrapper>
                    <StatusMessage>{message}</StatusMessage>
                </Wrapper>
            </PageLayout>
        );
    }

    const breadcrumbItems = [
        { link: ROUTES.courses(lang), title: COURSE_MESSAGES.courses },
        { link: ROUTES.course(lang, course.id), title: course.title },
        { link: "#", title: COURSE_MESSAGES.purchase }
    ];

    return (
        <>
            <SEO
                title={`${COURSE_MESSAGES.purchaseSeoTitle(course.title)} | کدپرو`}
                description={COURSE_MESSAGES.purchaseSeoDescription(course.title)}
                path={`/account/course/purchase/${course.id}`}
                noIndex
            />

            <PageLayout>
                <Breadcrumb items={breadcrumbItems} />

                <Wrapper>
                    <div className="flex flex-col lg:flex-row flex-1 w-full mt-5 gap-8">
                        <div className="w-full lg:w-1/2 flex flex-col gap-5">
                            {course.image && (
                                <ResponsiveImage
                                    className="rounded-md"
                                    src={course.image}
                                    alt={course.title}
                                />
                            )}

                            <h1 className="text-3xl font-bold mt-2">{course.title}</h1>

                            <CourseDifficulty difficulty={course.difficulty} />

                            <h2 className="mt-3 mb-2 text-2xl font-bold">
                                {COURSE_MESSAGES.description}
                            </h2>
                            <p className="text-lg text-gray-800 leading-9 whitespace-pre-line">
                                {course.description}
                            </p>

                            <h2 className="mt-8 mb-3 text-2xl font-bold">
                                {COURSE_MESSAGES.syllabus}
                            </h2>
                            <CourseSyllabus items={course.syllabus} showIcons />
                        </div>

                        <PurchasePanel course={course} onPay={handlePay} />
                    </div>

                    <CourseFaq items={course.faq} />
                </Wrapper>
            </PageLayout>
        </>
    );
};

export default PurchaseCoursePage;